import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, LogOut } from 'lucide-react';
import { ACTING_WINDOW_MS, useSession } from '@/core/session';
import { useT, useI18n } from '@/core/i18n';
import { nameOf } from '@/core/bilingual';
import { formatTimeAs } from '@/core/timeFormat';

/* =============================================================================
 * You are in somebody else's company
 * -----------------------------------------------------------------------------
 * The whole of the safety design that is visible to the person it protects
 * against — which is the operator themselves.
 *
 * An operator inside a company sees that company's theme, that company's
 * currency, that company's employees, and holds `*`. Every screen looks exactly
 * as it does to the customer, and that is deliberate: an operator helping
 * somebody should be looking at what that person is looking at. The cost of it
 * is that nothing else on the screen says whose data this is.
 *
 * So this does, on every page, in a colour no theme uses for anything else, and
 * it does not scroll away. The failure it exists to prevent is not subtle and
 * would not be recoverable: approving one company's payroll while believing it
 * to be another's.
 * ========================================================================== */

export function ActingBanner() {
  const t = useT();
  const { language } = useI18n();
  const { platform, company, leaveCompany } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  if (!platform?.actingCompanyId) return null;

  /* Computed rather than fetched. The session already carries acting_since and
     the window is a constant both sides agree on — a round trip to add twelve
     hours to a timestamp is a round trip to add twelve hours to a timestamp. */
  const endsAt = platform.actingSince
    ? new Date(new Date(platform.actingSince).getTime() + ACTING_WINDOW_MS)
    : null;

  async function out() {
    setBusy(true);
    try {
      /* Emptied on the way out as well as on the way in. What is cached right
         now is a customer's data, and the console is not the place for it to
         still be sitting. */
      queryClient.clear();
      await leaveCompany();
      navigate('/platform');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-40 bg-danger text-white"
    >
      <div className="px-3 sm:px-5 py-2 flex items-center gap-2.5 flex-wrap">
        <ShieldAlert size={15} className="shrink-0" />

        <p className="text-[12.5px] font-semibold min-w-0 flex-1 basis-[min(100%,14rem)] leading-snug">
          {t('plat.youAreInside', {
            company: company
              ? nameOf({ name: company.name, nameAr: company.nameAr }, language)
              : '',
          })}
          {platform.actingReason && (
            <span className="font-normal opacity-90"> — {platform.actingReason}</span>
          )}
          {/* When this stops working.
              -------------------------------------------------------------
              Being inside a company wears off after twelve hours — see
              migration 0110 — and the database simply stops answering at that
              moment. An operator who does not know that reads the silence as
              the app breaking, halfway through helping somebody. */}
          {endsAt && (
            <span className="font-normal opacity-90">
              {' · '}
              {t('plat.accessEnds', { time: formatTimeAs(endsAt) })}
            </span>
          )}
        </p>

        <button
          type="button"
          onClick={() => void out()}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold
                     px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25
                     disabled:opacity-60"
        >
          <LogOut size={12} />
          {t('plat.leave')}
        </button>
      </div>
    </div>
  );
}
